import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';

const SumitAccountCardsImportModal = ({
    open,
    onClose,
    previewSumitAccountCards,
    commitSumitAccountCards,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    user
}) => {
    const customStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
            boxSizing: 'border-box !important'
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: 'none',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black'
        }),
        singleValue: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        menu: (provided) => ({
            ...provided,
            textAlign: 'left',
            color: 'black',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#F9AA2A'
                : state.isFocused
                    ? '#ebebeb'
                    : null,
            color: 'black',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? 'darkgray' : 'black',
            '&:hover': {
                color: 'darkgray',
            },
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 2002,
        }),
    };

    // Format PalCodeType options
    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({
        label: type,
        value: type
    }));

    // Format PalCodeCategory options
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    const [sumitAccountCardsFile, setSumitAccountCardsFile] = useState(null);
    const [sumitAccountCardsEntries, setSumitAccountCardsEntries] = useState([]);
    const [sumitAccountCardsMissingPalCodes, setSumitAccountCardsMissingPalCodes] = useState([]);
    const [sumitAccountCardsPreviewDone, setSumitAccountCardsPreviewDone] = useState(false);
    const [sumitAccountCardsProcessing, setSumitAccountCardsProcessing] = useState(false);
    const [sumitAccountCardsParsedRows, setSumitAccountCardsParsedRows] = useState(0);

    // State for creating missing PalCodes
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({
        palCodeId: '',
        palCodeType: '',
        palCodeCategory: null
    });
    const [bulkForm, setBulkForm] = useState({
        palCodeType: '',
        palCodeCategory: null
    });

    // Calculate next orderNumber for a given category
    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;

        const palCodesInCategory = palCodes.filter(
            pc => pc.palCodeCategory === category
        );

        const maxOrderNumber = palCodesInCategory.reduce((max, pc) => {
            const orderNum = pc.orderNumber || 0;
            return Math.max(max, orderNum);
        }, 0);

        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleSumitAccountCardsDrop = files => {
        if (files?.length) setSumitAccountCardsFile(files[0]);
    };

    const handleSumitAccountCardsPreview = async () => {
        if (!sumitAccountCardsFile) return;

        try {
            setSumitAccountCardsProcessing(true);
            const res = await previewSumitAccountCards(sumitAccountCardsFile);
            const accountCards = res?.accountCards ?? [];
            const missingPalCodes = res?.missingPalCodes ?? [];
            const parsedRows = res?.parsedRows ?? 0;

            setSumitAccountCardsEntries(accountCards);
            setSumitAccountCardsMissingPalCodes(missingPalCodes);
            setSumitAccountCardsParsedRows(parsedRows);
            setSumitAccountCardsPreviewDone(true);
        } catch (err) {
            console.error('Sumit account cards preview failed:', err);
            setSumitAccountCardsPreviewDone(false);
        } finally {
            setSumitAccountCardsProcessing(false);
        }
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) {
            return;
        }

        try {
            setSumitAccountCardsProcessing(true);

            const calculatedOrderNumber = palCodeForm.palCodeCategory
                ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                : 100;

            const newPalCode = {
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: calculatedOrderNumber,
                accountantId: user.accountantId,
            };

            const response = await createMissingPalCode(newPalCode);
            if (!response.ok) {
                const errorText = await response.text();
                console.error("PalCode creation failed:", errorText);
                throw new Error(errorText);
            }

            // Remove from missing list
            setSumitAccountCardsMissingPalCodes(prev =>
                prev.filter(pc => pc !== palCodeName)
            );

            // Reset form
            setEditingPalCode(null);
            setPalCodeForm({
                palCodeId: '',
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('PalCode creation error:', err);
            throw err;
        } finally {
            setSumitAccountCardsProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) {
            return;
        }

        try {
            setSumitAccountCardsProcessing(true);

            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            const createPromises = selectedPalCodesForBulk.map((palCodeName, index) => {
                const newPalCode = {
                    palCodeId: palCodeName,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user.accountantId,
                };
                return createMissingPalCode(newPalCode);
            });

            const responses = await Promise.all(createPromises);
            const failed = responses.filter(r => !r.ok);

            if (failed.length > 0) {
                console.error("Some PalCodes failed to create");
                throw new Error(`${failed.length} PalCodes failed to create`);
            }

            // Remove created PalCodes from missing list
            setSumitAccountCardsMissingPalCodes(prev =>
                prev.filter(pc => !selectedPalCodesForBulk.includes(pc))
            );

            // Reset bulk mode
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({
                palCodeType: '',
                palCodeCategory: null
            });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
            throw err;
        } finally {
            setSumitAccountCardsProcessing(false);
        }
    };

    const handleSumitAccountCardsCommit = async () => {
        if (!sumitAccountCardsFile) return;

        try {
            setSumitAccountCardsProcessing(true);

            const res = await commitSumitAccountCards(sumitAccountCardsFile);

            if (res && res.errorMessages && res.errorMessages.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Sumit account cards commit error:', err);
        } finally {
            setSumitAccountCardsProcessing(false);
        }
    };

    const handleClose = () => {
        setSumitAccountCardsFile(null);
        setSumitAccountCardsPreviewDone(false);
        setSumitAccountCardsEntries([]);
        setSumitAccountCardsMissingPalCodes([]);
        setSumitAccountCardsParsedRows(0);
        setEditingPalCode(null);
        setPalCodeForm({
            palCodeId: '',
            palCodeType: '',
            palCodeCategory: null
        });
        onClose();
    };

    const startEditingPalCode = (palCodeName) => {
        setEditingPalCode(palCodeName);
        setPalCodeForm({
            palCodeId: palCodeName,
            palCodeType: '',
            palCodeCategory: null
        });
    };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: sumitAccountCardsPreviewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportSumitAccountCards') || 'ייבוא כרטיסי חשבון מ-Sumit'}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>{t('UploadFile')} (.xlsx/.xls)</h5>
                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                                {t('SumitAccountCardsFormat') || 'עמודה B: מזהה כרטיס, עמודה C: שם הכרטיס, עמודה D: חשבון ראשי'}
                            </p>
                            <DropzoneArea
                                key={sumitAccountCardsFile ? sumitAccountCardsFile.name : 'sumit-account-cards-empty'}
                                onDrop={handleSumitAccountCardsDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {sumitAccountCardsFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {sumitAccountCardsFile.name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handleSumitAccountCardsPreview}
                            disabled={!sumitAccountCardsFile || sumitAccountCardsProcessing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!sumitAccountCardsPreviewDone || sumitAccountCardsProcessing || sumitAccountCardsMissingPalCodes.length > 0}
                            onClick={handleSumitAccountCardsCommit}
                        >
                            {sumitAccountCardsProcessing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {sumitAccountCardsPreviewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{sumitAccountCardsParsedRows}</strong> |
                                    {' '}{t('AccountCards') || 'כרטיסי חשבון'}: <strong>{sumitAccountCardsEntries.length}</strong>
                                </p>
                            </div>

                            {/* Missing PalCodes Section - Exactly like ExcelAccountCardsImportModal */}
                            {sumitAccountCardsMissingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: '14px', color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? "contained" : "outlined"}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                if (!bulkMode) {
                                                    setBulkForm({
                                                        palCodeType: '',
                                                        palCodeCategory: null
                                                    });
                                                }
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {/* Bulk Creation Form */}
                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeType: selectedOption?.value || '' });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeCategory: selectedOption?.value || null });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: '12px', color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {sumitAccountCardsMissingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={sumitAccountCardsProcessing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {sumitAccountCardsMissingPalCodes.map((palCodeName, idx) => (
                                        <div key={idx} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                            {editingPalCode === palCodeName ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                        size="small"
                                                        fullWidth
                                                        disabled
                                                    />
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeType: selectedOption?.value || '' });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: '14px', fontWeight: 500 }}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </label>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeCategory: selectedOption?.value || null });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(palCodeName)}
                                                            disabled={sumitAccountCardsProcessing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                        >
                                                            {t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({
                                                                    palCodeId: '',
                                                                    palCodeType: '',
                                                                    palCodeCategory: null
                                                                });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {bulkMode && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPalCodesForBulk.includes(palCodeName)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedPalCodesForBulk(prev => [...prev, palCodeName]);
                                                                    } else {
                                                                        setSelectedPalCodesForBulk(prev => prev.filter(pc => pc !== palCodeName));
                                                                    }
                                                                }}
                                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            />
                                                        )}
                                                        <span style={{ fontWeight: 'bold' }}>{palCodeName}</span>
                                                    </div>
                                                    {!bulkMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => startEditingPalCode(palCodeName)}
                                                        >
                                                            {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Account Cards Table */}
                            {sumitAccountCardsEntries.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoAccountCardsFound') || 'לא נמצאו כרטיסי חשבון'}</p>
                            ) : (
                                <>
                                    <h5>{t('AccountCards') || 'כרטיסי חשבון'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sumitAccountCardsEntries.map((entry, i) => {
                                                const isMissingPalCode = sumitAccountCardsMissingPalCodes.includes(entry.palCode);
                                                return (
                                                    <tr key={i} style={{
                                                        backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9',
                                                        ...(isMissingPalCode ? { backgroundColor: '#fff3cd' } : {})
                                                    }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardId || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardName || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left', color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                            {entry.palCode || ''}
                                                            {isMissingPalCode && <span style={{ color: '#dc3545', marginLeft: 5 }}>*</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SumitAccountCardsImportModal;

